import React from 'react';

import cx from 'classnames';
import { useSelector } from 'react-redux';

import ItemLink from 'components/Layouts/Admin/Dashboard/Navigation/ItemLink';
import Divider from 'components/shared/Divider';
import Settings2 from 'components/shared/Icon/icons/Settings2';
import SignOut from 'components/shared/Icon/icons/SignOut';
import { ACCOUNT_TYPE_ADMIN, ACCOUNT_TYPE_OWNER, PRIVACY_POLICY_URL, TERMS_OF_SERVICE_URL } from 'constants/index';
import { SETTINGS_ACCOUNT_PATH, USERS_PATH, CONTENT_LIBRARY_PATH } from 'constants/routes';
import { APP_HAS_CHANGES_TEXT } from 'constants/texts';
import { useAccountPath } from 'hooks/useAccountPath';
import useMediaBreakpoints from 'hooks/useMediaBreakpoints';
import { appHasChangesSelector } from 'store/unsavedChanges/selectors';
import t from 'utils/translate';

import Icon from '../../../Icon';
import { prepareAccountsLinks, useMenu } from './functions';
import styles from './index.module.css';

const Links = ({ close }) => {
  const getPath = useAccountPath();
  const { user, handleLogout, changeAccount } = useMenu();
  const doesAppHaveChanges = useSelector(appHasChangesSelector);
  const { isTabletOrMobile } = useMediaBreakpoints();

  const handleChangeAccount = (accId, cb) => async () => {
    if (doesAppHaveChanges && !window.confirm(APP_HAS_CHANGES_TEXT)) {
      return;
    }

    if (cb) cb();
    changeAccount(accId);
  };

  const accountList = prepareAccountsLinks(user.accountList);

  return (
    <>
      <div>
        {accountList.map((acc) => {
          if (user.currentAccount.id !== acc.id) {
            return (
              <div className={styles.menuItemWrapper}>
                {acc.type === ACCOUNT_TYPE_ADMIN ? (
                  <ItemLink
                    href={getPath(USERS_PATH, { accountId: acc.id })}
                    exact
                    className={cx(styles.menuOption, styles.menuOptionUser)}
                    linkClassName={styles.menuOptionLink}
                    noUnderline
                    isSSR
                  >
                    <MenuItem company={`${t(`accountType.${acc.type}`)} Dashboard`} />
                  </ItemLink>
                ) : acc.type === ACCOUNT_TYPE_OWNER ? (
                  <ItemLink
                    href={getPath(CONTENT_LIBRARY_PATH, { accountId: acc.id })}
                    exact
                    className={cx(styles.menuOption, styles.menuOptionUser)}
                    linkClassName={styles.menuOptionLink}
                    noUnderline
                    isSSR
                  >
                    <MenuItem company={acc.companyName} type={t(`accountType.${acc.type}`)} />
                  </ItemLink>
                ) : (
                  <ItemLink
                    key={acc.id}
                    isButton
                    onClick={handleChangeAccount(acc.id, close)}
                    className={cx(styles.menuOption, styles.menuOptionUser)}
                    linkClassName={styles.menuOptionLink}
                    noUnderline
                  >
                    <MenuItem company={acc.companyName} type={t(`accountType.${acc.type}`)} />
                  </ItemLink>
                )}
              </div>
            );
          }

          return null;
        })}
        {!isTabletOrMobile && accountList.length > 1 && <Divider className={styles.divider} />}
        <div className={styles.menuItemWrapper}>
          <ItemLink
            href={getPath(SETTINGS_ACCOUNT_PATH)}
            className={styles.menuOption}
            linkClassName={styles.menuOptionLink}
            noUnderline
            isSSR
          >
            <Icon component={Settings2} fill="none" />
            Settings
          </ItemLink>
        </div>

        <div className={styles.menuItemWrapper}>
          <ItemLink
            isButton
            onClick={handleLogout}
            className={styles.menuOption}
            linkClassName={styles.menuOptionLink}
            noUnderline
          >
            <Icon component={SignOut} fill="none" />
            Logout
          </ItemLink>
        </div>

        {!isTabletOrMobile && <Divider className={styles.divider} />}

        <div className={cx(styles.container, styles.realLinks)}>
          <ItemLink isRealLink noMargin href={PRIVACY_POLICY_URL} linkClassName={styles.realLink} noUnderline>
            Terms & Policies
          </ItemLink>
          <ItemLink isRealLink noMargin href={TERMS_OF_SERVICE_URL} linkClassName={styles.realLink} noUnderline>
            Platform Terms of Service
          </ItemLink>
        </div>
      </div>
    </>
  );
};

export default Links;

function MenuItem({ company, type }) {
  return (
    <div className={styles.menuItem}>
      <div className={styles.company} title={company}>
        {company}
      </div>

      {type && <span className={styles.type}>({type})</span>}
    </div>
  );
}
